import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import captureException from 'utils/captureException';

import { parseTimeInput } from './timepickerFunctions';

// Ensure dayjs has the needed plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export const processTimeAndUpdateDate = (
  timeInputValue: string,
  currentDateValue: MaybeUndef<Date>,
  tzString?: string
): Maybe<Date> => {
  const parsed = parseTimeInput(timeInputValue);
  if (!parsed) return null;

  // If no timezone specified, use standard Date object
  if (!tzString) {
    let baseDate;

    // If it's a date-only string (YYYY-MM-DD), parse it as local date, not UTC
    if (
      typeof currentDateValue === 'string' &&
      /^\d{4}-\d{2}-\d{2}$/.test(currentDateValue)
    ) {
      baseDate = dayjs(currentDateValue).toDate();
    } else if (currentDateValue instanceof Date) {
      baseDate = new Date(currentDateValue);
    } else if (
      typeof currentDateValue === 'string' ||
      typeof currentDateValue === 'number'
    ) {
      baseDate = new Date(currentDateValue);
    } else {
      baseDate = new Date();
    }

    baseDate.setHours(parsed.hours, parsed.minutes, 0, 0);
    return baseDate;
  }

  // If timezone specified, use dayjs to handle it properly
  try {
    let baseDate;

    if (currentDateValue) {
      // If it's a date-only string (YYYY-MM-DD), parse it IN the target timezone
      if (
        typeof currentDateValue === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(currentDateValue)
      ) {
        baseDate = dayjs.tz(currentDateValue, tzString);
      } else {
        baseDate = dayjs(currentDateValue);
      }
    } else {
      baseDate = dayjs();
    }

    return baseDate
      .tz(tzString)
      .hour(parsed.hours)
      .minute(parsed.minutes)
      .second(0)
      .millisecond(0)
      .toDate();
  } catch (e) {
    captureException(e);
    const baseDate =
      currentDateValue instanceof Date
        ? new Date(currentDateValue)
        : new Date();
    baseDate.setHours(parsed.hours, parsed.minutes, 0, 0);
    return baseDate;
  }
};
